'use client';

import { fetcher } from '@insta-club/api';
import { ReelsSkeleton } from '@insta-club/components';
import { Reel, ReelsResponse } from '@insta-club/models';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, Mousewheel, Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import ReelsVideoPlayer from './reels-video-player';

interface Props {
    url: string;
    reelID?: number;
}

export default function Reels({ url, reelID }: Props) {
    const [isMuted, setIsMuted] = useState(true);
    const [slides, setSlides] = useState<Partial<Reel>[]>([]);
    const [instanceKey, setInstanceKey] = useState(() => Date.now());
    const isMountedRef = useRef(false);

    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            return;
        }
        setInstanceKey(Date.now());
    }, [url, reelID]);

    useEffect(() => {
        return () => {
            mutate([url, 1, reelID, instanceKey], undefined, { revalidate: false });
        };
    }, [url, reelID, instanceKey]);

    const fetchData = useCallback(async ([url, page, reelID]: [string, number, number]) => {
        return await fetcher(url, 'dashboard', 'GET', {
            params: { per_page: 5, page, reel_id: reelID }
        });
    }, []);

    const getKey = useCallback(
        (_: number, previousPageData: ReelsResponse | null) => {
            if (previousPageData && previousPageData.meta.current_page >= previousPageData.meta.last_page) {
                return null;
            }
            const nextPage = previousPageData ? previousPageData.meta.current_page + 1 : 1;
            return [url, nextPage, reelID, instanceKey];
        },
        [url, reelID, instanceKey]
    );

    const { data, size, setSize, isLoading, isValidating } = useSWRInfinite<ReelsResponse>(getKey, fetchData, {
        revalidateFirstPage: false,
        persistSize: false,
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateIfStale: false
    });

    const lastPage = useMemo(() => data?.[data.length - 1], [data]);
    const hasNextPage = useMemo(() => size < (lastPage?.meta?.last_page ?? 1), [size, lastPage]);

    useEffect(() => {
        const handleContextmenu = (e: PointerEvent) => e.preventDefault();
        document.addEventListener('contextmenu', handleContextmenu);
        return () => document.removeEventListener('contextmenu', handleContextmenu);
    }, []);

    useEffect(() => {
        if (!lastPage) return;
        const newSlides = lastPage.data ?? [];
        setSlides((prev) => {
            const prevFiltered = prev.filter((i) => !i.loader);
            return hasNextPage ? [...prevFiltered, ...newSlides, { loader: true }] : [...prevFiltered, ...newSlides];
        });
    }, [lastPage, hasNextPage]);

    if (isLoading) return <ReelsSkeleton />;

    return (
        <Swiper
            className='h-full w-full bg-black'
            direction='vertical'
            slidesPerView={1}
            modules={[Virtual, Mousewheel, Keyboard]}
            mousewheel
            keyboard
            virtual={{
                enabled: true,
                addSlidesAfter: 3,
                addSlidesBefore: 3
            }}
            onSlideChange={(swiper) => {
                const currentIndex = swiper.activeIndex;
                const currentSlide = swiper.virtual.slides[currentIndex];
                if (currentSlide?.props?.className === 'load-more-reels' && hasNextPage && !isValidating) {
                    setSize((prev) => prev + 1);
                }
            }}
        >
            {slides.map((slide, index) =>
                slide.loader ? (
                    <SwiperSlide key={`ss-${index}`} virtualIndex={index} className='load-more-reels'>
                        <ReelsSkeleton />
                    </SwiperSlide>
                ) : (
                    <SwiperSlide key={`ss-${index}`} virtualIndex={index}>
                        <ReelsVideoPlayer slide={slide as Reel} muted={isMuted} onToggleMute={setIsMuted} />
                    </SwiperSlide>
                )
            )}
        </Swiper>
    );
}

